import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import {
  AiOutlineClose,
  AiOutlineStar,
  AiFillStar,
  AiOutlineSend,
} from "react-icons/ai";

import { client } from "../../client";
import "./Feedback.scss";

const FeedbackModal = ({ isOpen, onClose }) => {
  const [feedbackData, setFeedbackData] = useState({
    name: "",
    email: "",
    company: "",
    position: "",
    feedback: "",
    rating: 0,
    imgUrl: "",
  });
  const [loading, setLoading] = useState(false);

  const { name, email, company, position, feedback, rating, imgUrl } =
    feedbackData;

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file" && name === "imgUrl") {
      // Handle file upload
      const file = files[0];
      if (file) {
        setFeedbackData({ ...feedbackData, [name]: file });
      }
    } else {
      // Handle regular text inputs
      setFeedbackData({ ...feedbackData, [name]: value });
    }
  };

  const handleRatingClick = (selectedRating) => {
    setFeedbackData({ ...feedbackData, rating: selectedRating });
  };

  const handleSubmitFeedback = async () => {
    // Validation
    if (!name.trim() || !email.trim() || !feedback.trim() || rating === 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please fill in all required fields and provide a rating!",
        confirmButtonColor: "#dc913a",
        customClass: {
          container: "swal-container-high-z",
        },
        didOpen: () => {
          const swalContainer = document.querySelector(".swal2-container");
          if (swalContainer) {
            swalContainer.style.zIndex = "20000";
          }
        },
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Email",
        text: "Please enter a valid email address!",
        confirmButtonColor: "#dc913a",
        customClass: {
          container: "swal-container-high-z",
        },
        didOpen: () => {
          const swalContainer = document.querySelector(".swal2-container");
          if (swalContainer) {
            swalContainer.style.zIndex = "20000";
          }
        },
      });
      return;
    }

    setLoading(true);

    try {
      let imageAsset = null;

      // Upload image if provided
      if (imgUrl && typeof imgUrl === "object") {
        imageAsset = await client.assets.upload("image", imgUrl, {
          filename: imgUrl.name,
        });
      }

      const testimonial = {
        _type: "testimonials",
        name,
        email,
        company: company || "N/A",
        position: position || "N/A",
        feedback,
        rating,
        imgUrl: imageAsset
          ? {
              _type: "image",
              asset: {
                _type: "reference",
                _ref: imageAsset._id,
              },
            }
          : undefined,
        approved: false,
        submittedAt: new Date().toISOString(),
      };

      await client.create(testimonial);

      setLoading(false);
      Swal.fire({
        icon: "success",
        title: "Feedback Submitted!",
        text: "Thank you for your feedback! It will be reviewed and published soon.",
        confirmButtonColor: "#dc913a",
        customClass: {
          container: "swal-container-high-z",
        },
        didOpen: () => {
          const swalContainer = document.querySelector(".swal2-container");
          if (swalContainer) {
            swalContainer.style.zIndex = "20000";
          }
        },
      });
      setFeedbackData({
        name: "",
        email: "",
        company: "",
        position: "",
        feedback: "",
        rating: 0,
        imgUrl: "",
      });
      onClose();
    } catch (err) {
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Submission Failed!",
        text: "Something went wrong. Please try again later.",
        confirmButtonColor: "#dc913a",
        customClass: {
          container: "swal-container-high-z",
        },
        didOpen: () => {
          const swalContainer = document.querySelector(".swal2-container");
          if (swalContainer) {
            swalContainer.style.zIndex = "20000";
          }
        },
      });
      console.error("Feedback submission error:", err);
    }
  };

  const renderStars = () => {
    return [...Array(5)].map((_, index) => {
      const starNumber = index + 1;
      return (
        <motion.button
          key={index}
          type="button"
          className={`star-btn ${starNumber <= rating ? "active" : ""}`}
          onClick={() => handleRatingClick(starNumber)}
          whileHover={{ scale: 1.2 }}
          whileTap={{ scale: 0.9 }}
        >
          {starNumber <= rating ? <AiFillStar /> : <AiOutlineStar />}
        </motion.button>
      );
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="feedback-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="feedback-modal-content"
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 50 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="feedback-modal-header">
            <h3>Share Your Feedback</h3>
            <button className="close-btn" onClick={onClose}>
              <AiOutlineClose />
            </button>
          </div>

          <div className="feedback-modal-body">
            <div className="feedback-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="feedback-name">Name *</label>
                  <input
                    id="feedback-name"
                    type="text"
                    placeholder="Your Full Name"
                    name="name"
                    value={name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="feedback-email">Email *</label>
                  <input
                    id="feedback-email"
                    type="email"
                    placeholder="your.email@example.com"
                    name="email"
                    value={email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="feedback-company">Company</label>
                  <input
                    id="feedback-company"
                    type="text"
                    placeholder="Your Company"
                    name="company"
                    value={company}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="feedback-position">Position</label>
                  <input
                    id="feedback-position"
                    type="text"
                    placeholder="Your Role/Position"
                    name="position"
                    value={position}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="feedback-imgUrl">
                  Profile Image (Optional)
                </label>
                <input
                  id="feedback-imgUrl"
                  type="file"
                  accept="image/*"
                  name="imgUrl"
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Rating *</label>
                <div className="rating-container">
                  {renderStars()}
                  <span className="rating-text">
                    {rating > 0 ? `${rating}/5` : "Please rate your experience"}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="feedback-message">Feedback *</label>
                <textarea
                  id="feedback-message"
                  placeholder="Share your experience working with me..."
                  name="feedback"
                  value={feedback}
                  onChange={handleInputChange}
                  rows="4"
                  required
                />
              </div>

              <motion.button
                type="button"
                className="submit-feedback-btn"
                onClick={handleSubmitFeedback}
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <AiOutlineSend />
                    Submit Feedback
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FeedbackModal;
